import { ICorpPerson, TravelerProfileProperties } from "redmond";

const isNonEmptyString = (value: string | undefined): value is string =>
  value != null && value.length > 0;

const programCodes = (map: Record<string, unknown> | undefined): string[] =>
  Object.keys(map ?? {});

const addedCodes = (previous: string[], next: string[]): string[] =>
  next.filter((code) => !previous.includes(code));

const removedCodes = (previous: string[], next: string[]): string[] =>
  previous.filter((code) => !next.includes(code));

export interface GetTravelerProfileTrackingPropertiesParams {
  person: ICorpPerson;
  previousPerson?: ICorpPerson;
}

export type TravelerProfileLoyaltyAndCompletenessProperties = Omit<
  TravelerProfileProperties,
  "source" | "entry_point" | "private"
>;

export const getTravelerProfileTrackingProperties = ({
  person,
  previousPerson,
}: GetTravelerProfileTrackingPropertiesParams): TravelerProfileLoyaltyAndCompletenessProperties => {
  const frequentFlyerCodes = programCodes(person.frequentFlyer);
  const previousFrequentFlyerCodes = programCodes(
    previousPerson?.frequentFlyer
  );
  const hotelLoyaltyCodes = programCodes(person.hotelLoyalty);
  const previousHotelLoyaltyCodes = programCodes(
    previousPerson?.hotelLoyalty
  );
  const carLoyaltyCodes = programCodes(person.groundLoyalty);
  const previousCarLoyaltyCodes = programCodes(previousPerson?.groundLoyalty);

  const carLoyaltyProgramsRemoved = removedCodes(
    previousCarLoyaltyCodes,
    carLoyaltyCodes
  );

  return {
    frequent_flyer_added: frequentFlyerCodes.length > 0,
    num_frequent_flyer_programs: frequentFlyerCodes.length,
    frequent_flyer_programs_added: addedCodes(
      previousFrequentFlyerCodes,
      frequentFlyerCodes
    ),
    hotel_loyalty_program_added: hotelLoyaltyCodes.length > 0,
    num_hotel_loyalty_programs: hotelLoyaltyCodes.length,
    hotel_loyalty_programs_added: addedCodes(
      previousHotelLoyaltyCodes,
      hotelLoyaltyCodes
    ),
    hotel_loyalty_programs_removed: removedCodes(
      previousHotelLoyaltyCodes,
      hotelLoyaltyCodes
    ),
    car_loyalty_program_added: carLoyaltyCodes.length > 0,
    car_loyalty_programs_in_profile: carLoyaltyCodes,
    num_car_loyalty_programs_added: addedCodes(
      previousCarLoyaltyCodes,
      carLoyaltyCodes
    ).length,
    num_car_loyalty_programs_removed: carLoyaltyProgramsRemoved.length,
    car_loyalty_programs_removed: carLoyaltyProgramsRemoved,
    is_default_profile: Boolean(person.isDefaultProfile),
    has_personal_details:
      isNonEmptyString(person.givenName) &&
      isNonEmptyString(person.surname) &&
      person.gender != null &&
      isNonEmptyString(person.dateOfBirth),
    has_contact_email: isNonEmptyString(person.emailAddress),
    has_tsa_precheck: isNonEmptyString(person.knownTravelerNumber),
    has_drivers_license:
      person.driverLicense != null &&
      isNonEmptyString(person.driverLicense.street) &&
      isNonEmptyString(person.driverLicense.cityCode) &&
      isNonEmptyString(person.driverLicense.country) &&
      isNonEmptyString(person.driverLicense.postCode),
    has_passport:
      person.passport != null &&
      isNonEmptyString(person.nationality?.country) &&
      isNonEmptyString(person.passport.countryOfIssue) &&
      isNonEmptyString(person.passport.number) &&
      isNonEmptyString(person.passport.expiration),
  };
};
